import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import { FormattedMessage, injectIntl } from 'react-intl';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import RunImportDialog from './RunImportDialog';
import SelectTableDialog from './SelectTableDialog';

const TYPE = 'TimescaleImport';

const MESSAGE = {
	FILE: { id: 'Extensions.TimescaleImport.File', defaultMessage: 'File' },
	FILE_PLACEHOLDER: {
		id: 'Extensions.TimescaleImport.FilePlaceholder',
		defaultMessage: 'Drop file here or select one'
	},
	ERROR_FILE_SIZE: { id: 'Extensions.TimescaleImport.FileError.Size', defaultMessage: 'File to big' },
	ERROR_FILE_TYPE: { id: 'Extensions.TimescaleImport.FileError.Type', defaultMessage: 'Unsupported file type' },
	TABLE: { id: 'Extensions.TimescaleImport.Table', defaultMessage: 'Table' },
	TABLE_PLACEHOLDER: {
		id: 'Extensions.TimescaleImport.TablePlaceholder',
		defaultMessage: 'Select or specify table for import'
	}
};
const ACCEPTED_MAX_SIZE = 1024 * 1024 * 1024; // GiB
const ACCEPTED_ENDINGS = ['csv', 'gz'];
const ACCEPTED_TYPES = [
	'application/gzip',
	'application/x-gzip',
	'text/csv',
	'text/plain',
	'text/x-csv',
	'text/comma-separated-values',
	'text/x-comma-separated-values',
	'text/tab-separated-values',
	'application/vnd.ms-excel',
	'application/csv',
	'application/x-csv'
];

const getFileEnding = (filename) => {
	const index = filename ? filename.lastIndexOf('.') + 1 : 0;
	return index > 0 ? filename.substring(index, filename.length) : undefined;
};

const validate = (file) => {
	const { name, size, type } = file || {};
	if (!ACCEPTED_ENDINGS.includes(getFileEnding(name)) || (type && !ACCEPTED_TYPES.includes(type))) {
		return MESSAGE.ERROR_FILE_TYPE;
	}
	return size > ACCEPTED_MAX_SIZE ? MESSAGE.ERROR_FILE_SIZE : undefined;
};

const TimescaleImportDialog = (props) => {
	const { intl, machine, open, onClose, info = {} } = props;
	const [file, setFile] = useState('');
	const [tableName, setTableName] = useState('');
	const [runImport, setRunImport] = useState(false);
	const [validateError, setValidateError] = useState('');
	const [isTableSelectOpen, setIsTableSelectOpen] = useState(false);
	const [fileSettings, setFileSettings] = useState({ isCompressed: false, hasHeader: true, delimiter: ',' });

	if (!open) {
		return null;
	}

	useEffect(() => {
		const name = info.params ? info.params[0] : undefined;
		setTableName(name);
	}, [props.info]);

	const setIsCompressed = (doIt) => {
		setFileSettings({ ...fileSettings, isCompressed: doIt });
	};
	const setHasHeader = (doIt) => {
		setFileSettings({ ...fileSettings, hasHeader: doIt });
	};
	const setDelimiter = (delimiter) => {
		setFileSettings({ ...fileSettings, delimiter });
	};

	const handleFileSelection = (files) => {
		const importFile = files[0];
		const compressed = !!importFile && importFile.name.endsWith('gz');
		setFile(importFile);
		setIsCompressed(compressed);
		setValidateError(validate(importFile));
	};

	const onCancel = () => onClose(TYPE);
	const onImport = () => {
		onClose();
		setRunImport(true);
	};
	const onImportDone = () => setRunImport(false);
	const onTableNameChange = (ev) => setTableName(ev.target.value.trim());
	const onTableSelect = (table) => setTableName(table);
	const openTableNameSelect = () => setIsTableSelectOpen(true);
	const closeTableSelect = () => setIsTableSelectOpen(false);

	if (!open) {
		return null;
	}

	return (
		<React.Fragment>
			<Dialog open={open}>
				<DialogTitle>
					<FormattedMessage id="Extensions.TimescaleImport.Dialog.Title" defaultMessage="Timescale import" />
				</DialogTitle>
				<DialogContent style={{ marginTop: '10px' }}>
					<TextField
						label={intl.formatMessage(MESSAGE.TABLE)}
						onChange={onTableNameChange}
						style={{ padding: '5px' }}
						placeholder={intl.formatMessage(MESSAGE.TABLE_PLACEHOLDER)}
						fullWidth
						value={tableName}
						margin="normal"
						InputLabelProps={{
							shrink: true
						}}
						InputProps={{
							endAdornment: (
								<Button color="primary" onClick={openTableNameSelect}>
									<FormattedMessage id="Extensions.Button.Select" defaultMessage="Select" />
								</Button>
							)
						}}
					/>
					<Dropzone
						onDrop={handleFileSelection}
						multiple={false}
						// accept={ACCEPTED_TYPES}
						// maxSize={ACCEPTED_MAX_SIZE}
						style={{ width: '100%' }}
					>
						<TextField
							label={intl.formatMessage(MESSAGE.FILE)}
							style={{ padding: '5px' }}
							placeholder={intl.formatMessage(MESSAGE.FILE_PLACEHOLDER)}
							error={!!validateError}
							helperText={validateError ? intl.formatMessage(validateError) : ''}
							fullWidth
							value={file ? file.name : ''}
							margin="normal"
							InputLabelProps={{
								shrink: true
							}}
						/>
					</Dropzone>
					<div style={{ width: '100%', height: '10px' }} />
					<Typography color="textSecondary" display="block" variant="caption" style={{ fontSize: '0.65rem' }}>
						<FormattedMessage id="Extensions.TimescaleImport.FileSettings" defaultMessage="File Settings" />
					</Typography>
					<div style={{ display: 'flex', padding: '5px', width: '100%', justifyContent: 'space-between' }}>
						<FormControlLabel
							style={{ marginLeft: '1px' }}
							control={
								<FormControl>
									<Select
										disabled={!!validateError || !file}
										value={fileSettings.delimiter}
										onChange={(event) => setDelimiter(event.target.value)}
									>
										<MenuItem value=",">Comma</MenuItem>
										<MenuItem value=";">Semicolon</MenuItem>
										<MenuItem value="t">Tab</MenuItem>
									</Select>
								</FormControl>
							}
							label={
								<Typography color="textPrimary">
									<FormattedMessage
										id="Extensions.TimescaleImport.FileSeparator"
										defaultMessage="Delimiter"
									/>
								</Typography>
							}
							labelPlacement="end"
						/>
						<FormControlLabel
							control={
								<Checkbox
									id="hideGrid"
									color="primary"
									disabled={!!validateError || !file}
									checked={fileSettings.isCompressed}
									onChange={(event, checked) => setIsCompressed(checked)}
								/>
							}
							label={
								<Typography color="textPrimary">
									<FormattedMessage
										id="Extensions.TimescaleImport.FileCompressed"
										defaultMessage="Compressed"
									/>
								</Typography>
							}
							labelPlacement="end"
						/>
						<FormControlLabel
							control={
								<Checkbox
									id="hideHeader"
									color="primary"
									disabled={!!validateError || !file}
									checked={fileSettings.hasHeader}
									onChange={(event, checked) => setHasHeader(checked)}
								/>
							}
							label={
								<Typography color="textPrimary">
									<FormattedMessage
										id="Extensions.TimescaleImport.FileHeader"
										defaultMessage="Contains Header"
									/>
								</Typography>
							}
							labelPlacement="end"
						/>
					</div>
				</DialogContent>
				<DialogActions>
					<Button color="primary" onClick={onCancel}>
						<FormattedMessage id="Extensions.Button.Cancel" defaultMessage="Cancel" />
					</Button>
					<Button color="primary" onClick={onImport} disabled={!!validateError || !tableName || !file}>
						<FormattedMessage id="Extensions.TimescaleImport.Button.Import" defaultMessage="Import" />
					</Button>
				</DialogActions>
			</Dialog>
			<SelectTableDialog
				open={isTableSelectOpen}
				machineId={machine.id}
				onClose={closeTableSelect}
				onSelect={onTableSelect}
			/>
			<RunImportDialog
				open={runImport}
				file={file}
				fileSettings={fileSettings}
				machineId={machine.id}
				table={tableName}
				onImportDone={onImportDone}
			/>
		</React.Fragment>
	);
};
TimescaleImportDialog.TYPE = TYPE;

function mapStateToProps(state) {
	return {
		machine: state.monitor.machine
	};
}
export default injectIntl(connect(mapStateToProps)(TimescaleImportDialog));



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/timescale/TimescaleImportDialog.js